import { Pause, Play, Stop, Television } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { findSpeaker, resolveMediaUrl } from '../lib/dialogue';
import {
  cuesForLine,
  mediaCueWhenLabel,
  mediaCuesOf,
} from '../lib/dialogueMediaCues';
import type { DialogueScript, PlacedObject } from '../types';

interface DialoguePlaybackBarProps {
  dialogue: DialogueScript;
  objects: PlacedObject[];
  activeLineId: string | null;
  paused?: boolean;
  progressKey?: number;
  onSeek: (index: number) => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

/** Read-only preview bar for dialogue playback (editing lives in the sheet panel). */
export function DialoguePlaybackBar({
  dialogue,
  objects,
  activeLineId,
  paused = false,
  progressKey = 0,
  onSeek,
  onPause,
  onResume,
  onStop,
}: DialoguePlaybackBarProps) {
  const activeIndex = dialogue.lines.findIndex((line) => line.id === activeLineId);
  const activeLine = activeIndex >= 0 ? dialogue.lines[activeIndex] : null;
  const [lineProgress, setLineProgress] = useState(0);
  const cues = mediaCuesOf(dialogue);

  useEffect(() => {
    if (!activeLine) {
      setLineProgress(0);
      return;
    }
    if (paused) return;

    const duration = Math.max(800, activeLine.durationMs);
    const startedAt = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const next = Math.min(1, (now - startedAt) / duration);
      setLineProgress(next);
      if (next < 1) frame = requestAnimationFrame(tick);
    };
    setLineProgress(0);
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [activeLine, paused, progressKey]);

  const overall = dialogue.lines.length
    ? (Math.max(activeIndex, 0) + (activeLine ? lineProgress : 0)) / dialogue.lines.length
    : 0;

  return (
    <div className="dialogue-playback-bar dialogue-playback-bar-preview" role="region" aria-label="대화 재생바">
      <div className="dialogue-playback-bar-track" aria-hidden="true">
        <div style={{ width: `${overall * 100}%` }} />
      </div>

      <div className="dialogue-playback-main">
        <div className="dialogue-playback-controls">
          {paused ? (
            <button
              type="button"
              className="dialogue-playback-pause"
              onClick={onResume}
              aria-label="재생 재개"
              title="재개"
            >
              <Play size={16} weight="fill" />
            </button>
          ) : (
            <button
              type="button"
              className="dialogue-playback-pause"
              onClick={onPause}
              aria-label="일시정지"
              title="일시정지"
            >
              <Pause size={16} weight="fill" />
            </button>
          )}
          <button
            type="button"
            className="dialogue-playback-stop"
            onClick={onStop}
            aria-label="재생 중지"
            title="중지"
          >
            <Stop size={16} weight="fill" />
          </button>
        </div>

        <ol className="dialogue-playback-steps">
          {dialogue.lines.map((line, index) => {
            const speaker = findSpeaker(dialogue, line.speakerId);
            const src = speaker ? resolveMediaUrl(speaker.image) : null;
            const active = line.id === activeLineId;
            const done = activeIndex >= 0 && index < activeIndex;
            const lineCues = cuesForLine(cues, line.id);
            return (
              <li key={line.id} className="dialogue-playback-step-wrap">
                <button
                  type="button"
                  className={`dialogue-playback-step ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                  onClick={() => onSeek(index)}
                  title={`${String(index + 1).padStart(2, '0')} · ${speaker?.name ?? '화자'}`}
                  aria-current={active ? 'true' : undefined}
                  aria-label={`${index + 1}번 대사${speaker ? `, ${speaker.name}` : ''}`}
                >
                  {active && (
                    <span
                      className="dialogue-playback-step-progress"
                      style={{ transform: `scaleX(${lineProgress})` }}
                    />
                  )}
                  <span className="dialogue-playback-step-num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="dialogue-playback-step-avatar">
                    {src ? <img src={src} alt="" /> : <span>{(speaker?.name ?? '?').slice(0, 1)}</span>}
                  </span>
                </button>
                {lineCues.length > 0 && (
                  <ul className="dialogue-playback-cue-list" aria-label={`${index + 1}번 컷 디스플레이`}>
                    {lineCues.map((cue) => {
                      const object = objects.find((item) => item.id === cue.objectId);
                      return (
                        <li key={cue.id}>
                          <span className="dialogue-playback-cue-chip readonly">
                            <Television size={11} weight="fill" />
                            <span className="dialogue-playback-cue-name">{object?.name ?? '기기'}</span>
                            <span className="dialogue-playback-cue-when">{mediaCueWhenLabel(cue.when)}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>

        <div className="dialogue-playback-meta" aria-live="polite">
          <strong>{Math.max(activeIndex + 1, 0)}</strong>
          <span>/ {dialogue.lines.length}</span>
        </div>
      </div>
    </div>
  );
}
